import { useEffect } from "react";
import { hapticSelection } from "../lib/haptics";
import { isNative } from "../lib/platform";
import { useSafeNavigate } from "./useSafeNavigate";

const EDGE_ZONE_PX = 24;
const TRIGGER_DISTANCE_PX = 70;
const MAX_VERTICAL_DRIFT_PX = 50;

/**
 * iOS-convention back gesture (native only): a rightward swipe starting at
 * the LEFT SCREEN EDGE navigates back through the router history. The WebView
 * has no browser chrome and Capacitor disables WKWebView's own back gesture,
 * so without this, pages without a back button are dead ends.
 *
 * Deliberately narrow: the swipe must begin inside the edge zone (so
 * horizontally scrolled charts/tables never trigger it), travel mostly
 * horizontally, and there must be router history to go back to (react-router
 * tracks the stack depth in history.state.idx — 0 means app entry point).
 */
export const useEdgeSwipeBack = (): void => {
  const navigate = useSafeNavigate();

  useEffect(() => {
    if (!isNative()) return;

    let startX = 0;
    let startY = 0;
    let tracking = false;

    const onTouchStart = (event: TouchEvent) => {
      // Surfaces that own their touch gestures (e.g. the Radar zoom/pan
      // canvas, where a pan can legitimately start at the left edge) opt out
      // with this attribute.
      if ((event.target as Element)?.closest?.("[data-native-gesture-optout]")) {
        tracking = false;
        return;
      }
      const touch = event.touches[0];
      tracking = touch.clientX <= EDGE_ZONE_PX;
      if (tracking) {
        startX = touch.clientX;
        startY = touch.clientY;
      }
    };

    const onTouchEnd = (event: TouchEvent) => {
      if (!tracking) return;
      tracking = false;
      const touch = event.changedTouches[0];
      const deltaX = touch.clientX - startX;
      const deltaY = Math.abs(touch.clientY - startY);
      const historyDepth = (window.history.state as { idx?: number } | null)?.idx ?? 0;
      if (deltaX >= TRIGGER_DISTANCE_PX && deltaY <= MAX_VERTICAL_DRIFT_PX && historyDepth > 0) {
        // The gesture has no visual transition, so the tick is the only cue
        // the swipe registered — same weight as a tab-bar tap.
        hapticSelection();
        navigate(-1);
      }
    };

    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchend", onTouchEnd, { passive: true });
    return () => {
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchend", onTouchEnd);
    };
  }, [navigate]);
};
